import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, ExternalLink, Linkedin, Mail, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import companyData from '../content/company.json';
import faqData from '../content/faq.json';
import ProtectedEmail from './ProtectedEmail';
import ProtectedPhone from './ProtectedPhone';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'de';
  const [isFaqOpen, setIsFaqOpen] = useState(false);
  const [openFaqItems, setOpenFaqItems] = useState<Set<string>>(new Set());
  
  const company = companyData[currentLang as keyof typeof companyData];
  const faqs = faqData[currentLang as keyof typeof faqData];
  const currentYear = new Date().getFullYear();

  const toggleFaqItem = (itemId: string) => {
    const newOpenItems = new Set(openFaqItems);
    if (newOpenItems.has(itemId)) {
      newOpenItems.delete(itemId);
    } else {
      newOpenItems.add(itemId);
    }
    setOpenFaqItems(newOpenItems);
  };
  
  return (
    <footer className="bg-slate-950 text-white border-t border-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-accent-orange flex items-center justify-center text-xl font-bold shadow-lg shadow-orange-500/20">
                P
              </div>
              <span className="text-xl font-bold tracking-tight">
                Plaschy Treuhand Beratungen GmbH
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-8 max-w-md">
              {t('footer.description')}
            </p>
            
            {/* Contact Info */}
            <div className="space-y-4 text-sm text-slate-300">
              <div className="flex items-start group">
                <MapPin className="w-5 h-5 mr-4 text-accent-orange shrink-0 mt-0.5 group-hover:text-white transition-colors" />
                <span className="leading-relaxed">Oberallmendstrasse 22<br />6300 Zug, Schweiz</span>
              </div>
              <div className="flex items-center group">
                <Phone className="w-5 h-5 mr-4 text-accent-orange shrink-0 group-hover:text-white transition-colors" />
                <ProtectedPhone 
                  className="hover:text-white transition-colors"
                  showIcon={false}
                  variant="text"
                  size="sm"
                />
              </div>
              <div className="flex items-center group">
                <Mail className="w-5 h-5 mr-4 text-accent-orange shrink-0 group-hover:text-white transition-colors" />
                <ProtectedEmail 
                  className="hover:text-white transition-colors"
                  showIcon={false}
                  variant="text"
                  size="md"
                />
              </div>
              <div className="flex items-center group">
                <Linkedin className="w-5 h-5 mr-4 text-accent-orange shrink-0 group-hover:text-white transition-colors" />
                <a 
                  href="https://www.linkedin.com/in/christian-richard-plaschy/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors flex items-center"
                >
                  <span className="mr-2">16K Follower</span>
                  <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>
            </div>
          </div>
          
          {/* Quick Links */}
          <div className="lg:pl-8">
            <h3 className="text-sm font-bold mb-6 uppercase tracking-wider text-slate-500">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to={`/${currentLang}`} className="text-slate-400 hover:text-accent-orange transition-colors block py-1">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to={`/${currentLang}/about`} className="text-slate-400 hover:text-accent-orange transition-colors block py-1">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to={`/${currentLang}/services`} className="text-slate-400 hover:text-accent-orange transition-colors block py-1">
                  {t('nav.services')}
                </Link>
              </li>
              <li>
                <Link to={`/${currentLang}/blog`} className="text-slate-400 hover:text-accent-orange transition-colors block py-1">
                  {t('nav.blog')}
                </Link>
              </li>
              <li>
                <Link to={`/${currentLang}/faq`} className="text-slate-400 hover:text-accent-orange transition-colors block py-1">
                  {t('nav.faq')}
                </Link>
              </li>
              <li>
                <Link to={`/${currentLang}/contact`} className="text-slate-400 hover:text-accent-orange transition-colors block py-1">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Services & FAQ */}
          <div>
            <h3 className="text-sm font-bold mb-6 uppercase tracking-wider text-slate-500">{t('footer.services')}</h3>
            <ul className="space-y-3 text-sm mb-8">
              <li>
                <Link to={`/${currentLang}/services/legacy-wealth`} className="text-slate-400 hover:text-accent-orange transition-colors block py-1">
                  {t('services.invest.title')}
                </Link>
              </li>
              <li>
                <Link to={`/${currentLang}/services/business-consulting`} className="text-slate-400 hover:text-accent-orange transition-colors block py-1">
                  {t('services.build.title')}
                </Link>
              </li>
              <li>
                <Link to={`/${currentLang}/services/ocpd-executive-coaching`} className="text-slate-400 hover:text-accent-orange transition-colors block py-1">
                  {t('services.scale.title')}
                </Link>
              </li>
            </ul>
            
            {/* Collapsible FAQ Section */}
            <div className="border-t border-slate-800 pt-6">
              <button
                onClick={() => setIsFaqOpen(!isFaqOpen)}
                className="flex items-center justify-between w-full text-left mb-4 group"
              >
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-accent-orange" />
                  <span className="text-sm font-bold uppercase tracking-wider text-slate-500 group-hover:text-slate-300 transition-colors">
                    {t('nav.faq')}
                  </span>
                </div>
                {isFaqOpen ? (
                  <ChevronUp className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                )}
              </button>
              
              {isFaqOpen && (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  {faqs.slice(0, 2).map((category, categoryIndex) => (
                    <div key={categoryIndex} className="space-y-2">
                      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        {category.category}
                      </h4>
                      {category.questions.slice(0, 2).map((faq, faqIndex) => {
                        const itemId = `footer-${categoryIndex}-${faqIndex}`;
                        const isOpen = openFaqItems.has(itemId);
                        
                        return (
                          <div key={faqIndex} className="border border-slate-800 overflow-hidden">
                            <button
                              onClick={() => toggleFaqItem(itemId)}
                              className="w-full text-left px-3 py-2 text-xs text-slate-400 hover:text-white transition-colors flex items-center justify-between"
                            >
                              <span className="pr-2 line-clamp-1">{faq.question}</span>
                              {isOpen ? (
                                <ChevronUp className="w-3 h-3 shrink-0" />
                              ) : (
                                <ChevronDown className="w-3 h-3 shrink-0" />
                              )}
                            </button>
                            {isOpen && (
                              <div className="px-3 pb-2 border-t border-slate-800">
                                <p className="text-xs text-slate-400 leading-relaxed pt-2">
                                  {faq.answer}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  <Link
                    to={`/${currentLang}/faq`}
                    className="block text-center text-xs text-accent-orange hover:text-orange-400 transition-colors pt-2"
                  >
                    {currentLang === 'de' ? 'Alle FAQs anzeigen →' : 'View all FAQs →'}
                  </Link>
                </div>
              )}
            </div>
            
            {/* CTA */}
            <div className="mt-8 pt-8 border-t border-slate-800">
              <a
                href={company.contact.appointment}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium transition-all hover:border-accent-orange/50 group"
              >
                {t('contact.info.appointment')}
                <ExternalLink className="w-4 h-4 ml-2 text-accent-orange group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-slate-900 mt-12 pt-8 text-xs text-slate-500">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p>
                {t('footer.copyright').replace('2025', currentYear.toString())}
              </p>
            </div>
            <div className="flex gap-6">
              <Link to={`/${currentLang}/privacy`} className="hover:text-white transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to={`/${currentLang}/imprint`} className="hover:text-white transition-colors">
                {t('footer.imprint')}
              </Link>
            </div>
          </div>
          <div className="mt-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-6 text-center md:text-left">
            <span>
              Design von{' '}
              <a
                href="https://otterino.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
              >
                Otterino
              </a>
            </span>
            <span className="hidden md:inline text-slate-700">·</span>
            <span>
              Digitalisierung von{' '}
              <a
                href="https://schnellstart.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
              >
                Schnellstart.ai
              </a>
            </span>
            <span className="hidden md:inline text-slate-700">·</span>
            <span>
              Blog Partner:{' '}
              <a
                href="https://swissfinance.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
              >
                SwissFinance.ai
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;



